import React, { useContext } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { LangContext } from "../../LanguageProvider";

// Должности на двух языках
const P = {
  economy: { kz: "экономика", ru: "экономика" },
  law: { kz: "құқық", ru: "право" },
  it: { kz: "ақпараттық технологиялар", ru: "информационные технологии" },
  kazakh: {
    kz: "қазақ тілі мен қазақ әдебиеті пәні оқытушысы",
    ru: "преподаватель казахского языка и литературы",
  },
  russian: {
    kz: "орыс тілі мен орыс әдебиеті пәні оқытушысы",
    ru: "преподаватель русского языка и литературы",
  },
  english: {
    kz: "ағылшын тілі пәні оқытушысы",
    ru: "преподаватель английского языка",
  },
  history: { kz: "тарих пәні оқытушысы", ru: "преподаватель истории" },
  math: { kz: "математика пәні оқытушысы", ru: "преподаватель математики" },
  chemBio: {
    kz: "химия және биология пәні оқытушысы",
    ru: "преподаватель химии и биологии",
  },
  pe: {
    kz: "дене тәрбиесі пәні оқытушысы",
    ru: "преподаватель физической культуры",
  },
  military: {
    kz: "алғашқы әскери дайындық пәні оқытушысы",
    ru: "преподаватель начальной военной подготовки",
  },
  psych: { kz: "психолог - маман", ru: "психолог - специалист" },
  radio: {
    kz: "Радиотехника, электроника және телекоммуникациялар",
    ru: "Радиотехника, электроника и телекоммуникации",
  },
  oil: { kz: "Мұнай-газ саласы", ru: "Нефтегазовая отрасль" },
  none: { kz: "—", ru: "—" },
};

const withIds = list =>
  list.map(([fullName, position], i) => ({ id: i + 1, fullName, position }));

const sections = [
  {
    title: {
      ru: "ПЦК информационных, экономических и правовых дисциплин",
      kz: "Ақпараттық, экономикалық және құқықтық пәндер ПЦК",
    },
    teachers: withIds([
      ["Рагалиева Жания Болатовна", P.economy],
      ["Ахонова Гульнара Кентибаевна", P.economy],
      ["Бисенова Екатерина Наримановна", P.law],
      ["Сапина Аигерим Болатовна", P.law],
      ["Бапиев Идеят", P.it],
      ["Смагулова Айгерим Женисхановна", P.it],
      ["Киланбекова Дилара Бисенбіқызы", P.it],
      ["Балғалиев Азат Батырғалиұлы", P.it],
    ]),
  },
  {
    title: {
      ru: "ПЦК общеобразовательных дисциплин",
      kz: "Жалпы білім беретін пәндер ПЦК",
    },
    teachers: withIds([
      ["Габдешова Мансия Наримановна", P.kazakh],
      ["Куаншалиева Райгуль Тлеккабыловна", P.kazakh],
      ["Шанкиева Бахыт Габдрашовна", P.russian],
      ["Ютиш Екатерина Александровна", P.english],
      ["Маратова Аида Маратовна", P.history],
      ["Сапа Мирдан Гимранұлы", P.history],
      ["Джумахметова Лилия Киреевна", P.math],
      ["Тасжанова Балжан Кенесовна", P.math],
      ["Конырбаева Гульнара Халесовна", P.chemBio],
      ["Давлетов Нурлан Темиргалиевич", P.pe],
      ["Нургалиев Кенжебек Жасгасбаевич", P.military],
      ["Бунькина Ольга Борисовна", P.psych],
      ["Ескендиров Жәнгір Алмабекұлы", P.pe],
    ]),
  },
  {
    title: {
      ru: "ПЦК технических дисциплин",
      kz: "Техникалық пәндер ПЦК",
    },
    teachers: withIds([
      ["Айтмұханбетова Анара Талаповна", P.radio],
      ["Беккалиев Ақболат Ербулотович", P.radio],
      ["Галламова Айгерим Алтаевна", P.oil],
      ["Мақсотова Айгерим Тасболатовна", P.radio],
      ["Садықова Жанар Бейсеновна", P.radio],
      ["Сариева Ботагөз Қайратқызы", P.radio],
      ["Супалдиярова Гульмира Есмагамбетовна", P.none],
      ["Ескендирова Дильназ Куанышовна", P.radio],
      ["Камышева Нургуль Нурмахамбетовна", P.radio],
      ["Жолмуханов Бексултан Дарменович", P.radio],
      ["Тулемисова Самал Серикқызы", P.oil],
      ["Сисалиева Манар Бейбиталиевна", P.oil],
    ]),
  },
];

const formatPCKName = name => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 3) {
    const lastPart = parts.pop();
    return (
      <>
        {parts.join(" ")}
        <br />
        {lastPart}
      </>
    );
  }
  return name;
};

function TeacherTable({ teachers, lang }) {
  const head = { fontWeight: "bold" };
  return (
    <TableContainer component={Paper} sx={{ height: "100%" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={head}>
              №
            </TableCell>
            <TableCell align="center" sx={head}>
              {lang === "ru" ? "Ф.И.О." : "Т.А.Ә."}
            </TableCell>
            <TableCell align="center" sx={head}>
              {lang === "ru" ? "Должность" : "Лауазымы"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teachers.map(teacher => (
            <TableRow key={teacher.id}>
              <TableCell align="center">{teacher.id}</TableCell>
              <TableCell align="center">
                {formatPCKName(teacher.fullName)}
              </TableCell>
              <TableCell align="center">{teacher.position[lang]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CommissionPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { lang } = useContext(LangContext);

  return (
    <Container maxWidth="md" sx={{ marginTop: 12, marginBottom: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {lang === "ru"
            ? "Предметно-цикловая комиссия РВТК"
            : "РЖТК пәндік-циклдік комиссиясы"}
        </Typography>
        <Typography variant="h6">
          {lang === "ru" ? "2026-2027 учебный год" : "2026-2027 оқу жылы"}
        </Typography>
      </Box>

      <Box mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {lang === "ru" ? "ОТДЕЛЕНИЕ" : "БӨЛІМ"}
        </Typography>
        <Typography variant="body1">
          {lang === "ru"
            ? "IT-технологий, нефти, газа, экономики и права"
            : "IT-технологиялар, мұнай, газ, экономика және құқық"}
          <br />
          {lang === "ru"
            ? "Телекоммуникаций и транспорта"
            : "Телекоммуникация және транспорт"}
        </Typography>
      </Box>

      <Box mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          ПЦК
        </Typography>
        {sections.map((section, idx) => {
          const half = Math.ceil(section.teachers.length / 2);
          return (
            <React.Fragment key={idx}>
              <Typography
                variant="body1"
                sx={{ mb: 3, mt: idx ? 4 : 0, fontWeight: "bold" }}>
                {section.title[lang]}
              </Typography>
              <Grid container spacing={4}>
                {isMobile ? (
                  <Grid item xs={12}>
                    <TeacherTable teachers={section.teachers} lang={lang} />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} md={6}>
                      <TeacherTable
                        teachers={section.teachers.slice(0, half)}
                        lang={lang}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TeacherTable
                        teachers={section.teachers.slice(half)}
                        lang={lang}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </React.Fragment>
          );
        })}
      </Box>
    </Container>
  );
}

export default CommissionPage;
